<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  Loading02Icon
} from 'front-library'
import {
  getCopyPayrollRequests,
  CopyPayrollRequest
} from '@/app/core/services/payroll-copy-requests'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const listErrors = ref()

const loading = ref(false)

/* Datos */
const search = ref()
const tableCopyPayrollRequest = ref(null)
const refresh = ref()
const isRefreshData = ref<boolean>(false)

/* Datos componente table */
const columns = ref([
  {
    name: 'payroll_origin',
    title: t('payrollCopyRequests.list.sourcePayroll'),
    table: 'payroll_origin'
  },
  {
    name: 'payroll_destination',
    title: t('payrollCopyRequests.list.destinationPayroll'),
    table: 'payroll_destination'
  },
  {
    name: 'concepts',
    title: t('payrollCopyRequests.list.concepts'),
    table: 'concepts'
  },
  {
    name: 'status',
    title: t('payrollCopyRequests.list.status'),
    table: 'status_code'
  },
  {
    name: 'concept_summary',
    title: t('payrollCopyRequests.list.conceptSummary'),
    table: 'concept_summary'
  },
  {
    name: 'base_summary',
    title: t('payrollCopyRequests.list.baseSummary'),
    table: 'base_summary'
  },
  {
    name: 'updated_at',
    title: t('payrollCopyRequests.list.lastUpdate'),
    table: 'payroll_copy_requests.updated_at'
  },
  {
    name: 'list_errors',
    title: t('payrollCopyRequests.list.errors'),
    table: 'errors'
  }
])
const rows = ref<CopyPayrollRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createCopyPayrollRequest = () => {
  void router.push({ name: 'CreateCopyPayrollRequest' })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar solicitudes de copiado de nominas */
const copyPayrollRequests = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  try {
    const data: any = await getCopyPayrollRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results

    rows.value = data.results.map((item: any) => {
      if (item.errors) {
        item.errors = item.errors.map((error: any) => {
          const { id, ...rest } = error

            for (const key in rest) {
              if (rest.hasOwnProperty(key) && t(`payrollCopyRequests.errors.${key}`)) {
                rest[t(`payrollCopyRequests.errors.${key}`)] = rest[key]
                delete rest[key]
              }
            }

          return rest
        })
      }
      return item
    })

    totalRows.value = data.totalItems
    loading.value = false
    setTimeout(() => {
      isRefreshData.value = false
    }, 2000)
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void copyPayrollRequests(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    :title="t('payrollCopyRequests.list.errorList')"
    :size="'max-w-[calc(100vw-30rem)]'"
    @closeModal="closeModal">
    <template v-slot:description>
      <div class="text-left px-5" style="max-height: calc(100vh - 20rem) !important; max-width: calc(100vw - 30rem) !important;">
        <div
          v-for="(error, index) in listErrors"
          :key="index"  
          class="text-gray-600">
          <span class="text-red-700">&#10006;</span> {{ error }}
        </div>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('payrollCopyRequests.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('payrollCopyRequests.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="copyPayrollRequests()" />
          <DBtn
            @click.prevent="copyPayrollRequests()"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createCopyPayrollRequest()"
          v-if="store.hasPermission('payroll_copy_request:bulk')">
          {{
            t('form.button.create', {
              form: t('payrollCopyRequests.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableCopyPayrollRequest"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), copyPayrollRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), copyPayrollRequests()">
      <template #list_errors="data: any">
        <div class="my-1.5">
          <div v-if="data.errors.length === 1">
            <p class="text-sm text-gray-500">
              <span class="text-red-700">&#10006;</span> {{ data.errors[0] }}
            </p>
          </div>
          <div
            v-if="data.errors.length > 1"
            class="bg-red-50 rounded-lg text-red-700 text-sm font-semibold py-1 px-2 mt-1.5 w-max cursor-pointer"
            @click=";(showModal = true), (listErrors = data.errors)">
            <p>
              {{ t('payrollCopyRequests.list.examineErrors') }}
              ({{ data.errors.length }})
            </p>
          </div>
        </div>
      </template>
      <template #status="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status_code === 'NOSTART'
              ? 'bg-gray-100 text-gray-600'
              : data.status_code === 'PENDING'
              ? 'bg-blue-100 text-blue-600'
              : data.status_code === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="
              data.status_code === 'NOSTART'
                ? 'bg-gray-600'
                : data.status_code === 'PENDING'
                ? 'bg-blue-600'
                : data.status_code === 'ERROR'
                ? 'bg-red-600'
                : 'bg-green-600'
            "></div>
          {{ t('status.' + data.status_code) }}
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('loading.updated')" />
      </template>
    </DTable>
  </div>
</template>
